/**
 * Hook for fetching Withings weight data
 * Transforms raw Withings API response to our internal format
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../stores/authStore'
import { useAdaptivePolling, createBackgroundRateLimiter } from '../../utils/rate-limiting'
import { queryKeys } from '../queryKeys'
import { useWithingsConnection } from './useWithingsAuth'

interface WithingsWeightMeasure {
  value: number
  unit: number
}

interface WithingsMeasureGroup {
  category: number
  date: number
  measures: WithingsWeightMeasure[]
}

interface WithingsAPIResponse {
  body: {
    measuregrps: WithingsMeasureGroup[]
  }
}

export interface WeightDataPoint {
  date: Date
  weight: number // in kg
}

// Relaxed rate limiting for Withings API - protects external service
const withingsRateLimit = createBackgroundRateLimiter('withings_weight', 60000) // 1 minute minimum

/**
 * Fetch 3-month weight data from Withings API (default baseline)
 * This is used for stable metric calculations (14-day balance, trends, etc.)
 */
export function useWithingsWeightData() {
  const { user } = useAuth()
  const { data: isConnected } = useWithingsConnection()
  const days = 90 // Always fetch 3 months for baseline analysis
  const adaptiveInterval = useAdaptivePolling(180000) // 3 minutes base, 9 minutes when idle

  return useQuery({
    queryKey: [...queryKeys.user(user?.id || ''), 'withings', 'weight', '3months'],
    queryFn: async (): Promise<WeightDataPoint[]> => {
      if (!user) throw new Error('Not authenticated')

      // Relaxed protection for Withings API - only blocks rapid successive calls
      if (!withingsRateLimit.shouldExecute()) {
        // Return cached data if available to avoid hitting external API
        throw new Error('Withings API call too frequent, using cached data')
      }

      // Calculate date range
      const endDate = Math.floor(Date.now() / 1000)
      const startDate = endDate - days * 24 * 60 * 60

      // Fetch from edge function using Supabase wrapper
      const { data, error } = await supabase.functions.invoke('withings-api', {
        body: {
          action: 'weight',
          startdate: startDate,
          enddate: endDate
        }
      })

      if (error) {
        throw new Error(error.message || 'Failed to fetch weight data')
      }

      // Handle raw Withings API response format
      const typedData = data as WithingsAPIResponse
      if (!typedData.body?.measuregrps || !Array.isArray(typedData.body.measuregrps)) {
        return []
      }

      // Transform raw Withings data to our format
      return typedData.body.measuregrps
        .filter(
          (group: WithingsMeasureGroup) =>
            group.category === 1 && // Real measurements only
            group.measures?.[0]?.value // Has valid weight measurement
        )
        .map((group: WithingsMeasureGroup) => {
          const measure = group.measures[0]
          const value = measure.value
          const unit = measure.unit

          // Dynamic weight conversion based on unit field
          // The unit field represents the power of 10 to apply to get the actual value
          // Examples:
          //   value: 78608, unit: -3 → 78608 * 10^(-3) = 78.608 kg
          //   value: 77533996, unit: -6 → 77533996 * 10^(-6) = 77.534 kg
          //   value: 80000, unit: -3 → 80000 * 10^(-3) = 80.0 kg
          // This handles any unit dynamically without hardcoded conversions
          const weightInKg = value * Math.pow(10, unit)

          return {
            date: new Date(group.date * 1000), // Convert Unix timestamp to Date
            weight: weightInKg
          }
        })
        .filter((point: WeightDataPoint) => {
          // Filter out unrealistic weight values (likely conversion errors)
          return point.weight > 20 && point.weight < 500 // Reasonable human weight range in kg
        })
        .sort((a: WeightDataPoint, b: WeightDataPoint) => a.date.getTime() - b.date.getTime())
    },
    enabled: !!user && !!isConnected,
    staleTime: adaptiveInterval, // Adaptive staleness based on user activity
    refetchInterval: adaptiveInterval, // Polls faster when user is active
    retry: 1,
    refetchOnWindowFocus: false // Prevent excessive calls when switching tabs
  })
}

/**
 * Fetch all-time weight data from Withings API
 * This is used when user selects "All Time" view for historical perspective
 */
export function useWithingsWeightAllTime(enabled = false) {
  const { user } = useAuth()
  const { data: isConnected } = useWithingsConnection()

  return useQuery({
    queryKey: [...queryKeys.user(user?.id || ''), 'withings', 'weight', 'alltime'],
    queryFn: async (): Promise<WeightDataPoint[]> => {
      if (!user) throw new Error('Not authenticated')

      // Fetch all available data (no date range limits)
      const { data, error } = await supabase.functions.invoke('withings-api', {
        body: {
          action: 'weight'
          // No startdate/enddate = fetch all available data
        }
      })

      if (error) {
        throw new Error(error.message || 'Failed to fetch all-time weight data')
      }

      // Handle raw Withings API response format
      const typedData = data as WithingsAPIResponse
      if (!typedData.body?.measuregrps || !Array.isArray(typedData.body.measuregrps)) {
        return []
      }

      // Transform raw Withings data to our format
      return typedData.body.measuregrps
        .filter(
          (group: WithingsMeasureGroup) =>
            group.category === 1 && // Real measurements only
            group.measures?.[0]?.value // Has valid weight measurement
        )
        .map((group: WithingsMeasureGroup) => {
          const measure = group.measures[0]
          const value = measure.value
          const unit = measure.unit

          // Dynamic weight conversion based on unit field
          // The unit field represents the power of 10 to apply to get the actual value
          // Examples:
          //   value: 78608, unit: -3 → 78608 * 10^(-3) = 78.608 kg
          //   value: 77533996, unit: -6 → 77533996 * 10^(-6) = 77.534 kg
          //   value: 80000, unit: -3 → 80000 * 10^(-3) = 80.0 kg
          // This handles any unit dynamically without hardcoded conversions
          const weightInKg = value * Math.pow(10, unit)

          return {
            date: new Date(group.date * 1000), // Convert Unix timestamp to Date
            weight: weightInKg
          }
        })
        .filter((point: WeightDataPoint) => {
          // Filter out unrealistic weight values (likely conversion errors)
          return point.weight > 20 && point.weight < 500 // Reasonable human weight range in kg
        })
        .sort((a: WeightDataPoint, b: WeightDataPoint) => a.date.getTime() - b.date.getTime())
    },
    enabled: !!user && !!isConnected && enabled, // Only fetch when explicitly enabled and connected
    staleTime: 30 * 60 * 1000, // Consider data stale after 30 minutes (longer for all-time data)
    retry: 1
  })
}
